import { isStaff } from "../roles";
import { useT } from "../i18n";
import { useServiceText } from "../service-text";
import Amount from "../components/Amount";
import {
  ArrowRight,
  CheckCircle2,
  CircleDollarSign,
  Clock3,
  Coins,
  FolderClock,
  Plus,
  WalletCards,
} from "lucide-react";
import { Link } from "wouter";
import DueCell from "../components/DueCell";
import Explain from "../components/Explain";
import OnTrack from "../components/OnTrack";
import CreditMeter from "../components/CreditMeter";
import StatusBadge from "../components/StatusBadge";
import type { DashboardData, SessionUser } from "../types";
import { formatCredits, formatDate, monthLabel, sourceLabelKey } from "../utils";

export default function OverviewPage({
  data,
  user,
  onNewRequest,
}: {
  data: DashboardData;
  user: SessionUser;
  onNewRequest: () => void;
}) {
  const t = useT();
  const st = useServiceText();
  const { balance } = data;
  const recentRequests = data.requests.slice(0, 5);
  const spendTotal = Math.max(balance.spent, 1);
  const pending = data.requests.filter((request) => request.status === "pending").length;

  return (
    <div className="page-stack">
      <section className="overview-intro">
        <div>
          <span className="section-eyebrow">{monthLabel(data.month)}</span>
          <h2>{isStaff(user.role) ? t("overview.staffTitle") : t("overview.title")}</h2>
          <p>
            {isStaff(user.role)
              ? t("overview.staffIntro", { n: pending })
              : t("overview.intro")}
          </p>
        </div>
        <button className="button button-primary" onClick={onNewRequest}><Plus size={18} /> {t("shell.requestService")}</button>
      </section>

      <section className="metric-grid" aria-label={t("overview.summary")}>
        <article className="metric-card metric-primary">
          <div className="metric-icon"><WalletCards size={21} /></div>
          <span>{t("overview.availableCredits")} <Explain of="available" /></span>
          <strong>{formatCredits(balance.available)}</strong>
          <small>{t("overview.readyToUse")}</small>
        </article>
        <article className="metric-card">
          <div className="metric-icon neutral"><CircleDollarSign size={21} /></div>
          <span>{t("overview.creditsSpent")} <Explain of="spent" /></span>
          <strong>{formatCredits(balance.spent)}</strong>
          <small>{t("overview.completedWork")}</small>
        </article>
        <article className="metric-card">
          <div className="metric-icon amber"><Clock3 size={21} /></div>
          <span>{t("overview.creditsReserved")} <Explain of="reserved" /></span>
          <strong>{formatCredits(balance.reserved)}</strong>
          <small>{t("overview.inProgressWork")}</small>
        </article>
        <article className="metric-card">
          <div className="metric-icon green"><Coins size={21} /></div>
          <span>{t("overview.totalPool")} <Explain of="totalPool" align="end" /></span>
          <strong>{formatCredits(balance.totalPool)}</strong>
          <small>{t("overview.poolBreakdown", { base: formatCredits(data.account.monthlyCredits), added: formatCredits(balance.carriedIn + balance.topups) })}</small>
        </article>
      </section>

      {/* Balance alone cannot answer "are we on track" — this can. */}
      <OnTrack month={data.month} />

      <section className="overview-grid">
        <article className="panel credit-position-panel">
          <div className="panel-header">
            <div>
              <span className="section-eyebrow">{t("overview.creditPosition")}</span>
              <h3>{t("overview.readyHeading", { n: formatCredits(balance.available) })}</h3>
            </div>
          </div>
          <CreditMeter available={balance.available} reserved={balance.reserved} spent={balance.spent} total={balance.totalPool} />
          <div className="balance-facts">
            <div><span>{t("overview.monthlyAllocation")} <Explain of="allocation" /></span><strong>{formatCredits(data.account.monthlyCredits)}</strong></div>
            <div><span>{t("overview.carriedForward")} <Explain of="carriedIn" /></span><strong>{formatCredits(balance.carriedIn)}</strong></div>
            <div><span>{t("overview.additionalCredits")} <Explain of="topups" align="end" /></span><strong>{formatCredits(balance.topups)}</strong></div>
          </div>
        </article>

        <article className="panel spend-panel">
          <div className="panel-header compact">
            <div>
              <span className="section-eyebrow">{t("overview.whereWent")}</span>
              <h3>{t("overview.byCategory")}</h3>
            </div>
            <Link href="/ledger" className="text-link">{t("nav.ledger")} <ArrowRight size={16} /></Link>
          </div>
          {data.categorySpend.length ? (
            <div className="category-bars">
              {data.categorySpend.slice(0, 5).map((item) => (
                <div className="category-bar-row" key={item.category}>
                  <div><span>{st.category(item.category)}</span><strong><Amount value={item.credits} unit={t("common.cr")} /></strong></div>
                  <div className="bar-track"><span style={{ width: `${Math.max(4, (item.credits / spendTotal) * 100)}%` }} /></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-compact">
              <CheckCircle2 size={24} />
              <div><strong>{t("overview.noSpendTitle")}</strong><span>{t("overview.noSpendBody")}</span></div>
            </div>
          )}
        </article>
      </section>

      <section className="panel recent-panel">
        <div className="panel-header compact">
          <div>
            <span className="section-eyebrow">{t("overview.latestActivity")}</span>
            <h3>{t("overview.requestsFor", { month: monthLabel(data.month) })}</h3>
          </div>
          <Link href="/requests" className="text-link">{t("overview.viewAll")} <ArrowRight size={16} /></Link>
        </div>
        {recentRequests.length ? (
          <div className="responsive-table-wrap">
            <table className="data-table">
              <thead><tr><th>{t("table.request")}</th><th>{t("table.source")}</th><th>{t("table.credits")}</th><th>{t("table.due")}</th><th>{t("table.status")}</th></tr></thead>
              <tbody>
                {recentRequests.map((request) => (
                  <tr key={request.id}>
                    <td data-label={t("table.request")}><strong dir="auto">{request.title}</strong><span>{st.name(request.service_id, request.service_name)}</span></td>
                    <td data-label={t("table.source")}>{t(sourceLabelKey(request.source))}</td>
                    <td data-label={t("table.credits")}>{formatCredits(request.final_credits ?? request.estimated_credits)}</td>
                    <td data-label={t("table.due")}><DueCell request={request} /></td>
                    <td data-label={t("table.status")}><StatusBadge status={request.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <FolderClock size={30} />
            <h4>{t("overview.noRequestsTitle")}</h4>
            <p>{t("overview.noRequestsBody")}</p>
            <button className="button button-secondary" onClick={onNewRequest}>{t("overview.createFirst")}</button>
          </div>
        )}
      </section>
    </div>
  );
}
